// CHA-619 — live, read-only subscription to ONE run's event trail.
//
// Platform: the caller (a tenant shell) names the tenant; this hook never guesses one. It reads
// tenants/{tenantId}/runs/{runId}/events with the client SDK under the existing rules (members of
// the tenant and platform roles may read; clients may never write). There is no write path here.
//
// Tenant/run switch safety (the CHA-616 lesson): results are keyed to the tenant and run they were
// read for and hidden on the first render after either changes, and a snapshot from a listener that
// has already been torn down is ignored, so one run's (or tenant's) trail can never be painted under
// another.
import { useEffect, useState } from 'react';
import { getFirestore, collection, onSnapshot } from 'firebase/firestore';

const millisOf = (v) => {
  if (v && typeof v.toMillis === 'function') return v.toMillis();
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'string') { const t = Date.parse(v); return Number.isFinite(t) ? t : null; }
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  return null;
};

// Order (Ron, 2026-09-26): `seq` first — the runtime stamps it on the block walk, so it is the run's
// own sequence. Time only breaks ties among events without a seq (or with an equal one): event times
// are not the ordering authority, and a vendor-supplied date must never reorder the trail. An event
// without a timestamp yet (a pending server write) sorts after timed ones; the doc id makes the order
// total and stable.
export function orderRunEvents(events) {
  return [...events].sort((a, b) => {
    const sa = Number.isFinite(a?.data?.seq) ? a.data.seq : Infinity;
    const sb = Number.isFinite(b?.data?.seq) ? b.data.seq : Infinity;
    if (sa !== sb) return sa - sb;
    const ta = millisOf(a.at); const tb = millisOf(b.at);
    if (ta !== tb) return (ta ?? Infinity) - (tb ?? Infinity);
    return String(a.id).localeCompare(String(b.id));
  });
}

export default function useRunEvents(tenantId, runId) {
  // Results are stored WITH the tenant and run they belong to, and returned only while those still
  // match the inputs. So on an in-place tenant/run change, the very first render with the new ids
  // already shows nothing from the old run — not only after an effect has cleared it.
  const [state, setState] = useState({ tenantId: null, runId: null, events: [], error: null });

  useEffect(() => {
    if (!tenantId || !runId) return undefined;
    let current = true;
    const db = getFirestore();
    const unsub = onSnapshot(
      collection(db, 'tenants', tenantId, 'runs', runId, 'events'),
      (snap) => {
        if (!current) return;
        setState({ tenantId, runId, events: orderRunEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), error: null });
      },
      (err) => {
        if (!current) return;
        console.error('run events subscription failed', err);
        setState({ tenantId, runId, events: [], error: 'Could not load this run’s history.' });
      },
    );
    return () => { current = false; unsub(); };
  }, [tenantId, runId]);

  if (!tenantId || !runId) return { events: [], loading: false, error: null };
  if (state.tenantId !== tenantId || state.runId !== runId) return { events: [], loading: true, error: null };
  return { events: state.events, loading: false, error: state.error };
}
